import { useMemo, useState } from 'react';
import { SegmentGroup } from 'semantic-ui-react';
import { FiltersBar, SuiTable, SuiTableState } from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';

import { useExaminerId } from 'components';
import { OsmMonitoringStatus, OsmMonitoringDto, Permission } from 'api';
import {
  useOsmMonitoringListQuery,
  useUpdateOsmMonitoringFollowUpMutation,
  useBulkDeleteOsmMonitoringMutation,
} from 'store/monitoring/osmMonitoringApi';
import { columns } from './OsmMonitoringList.columns';
import {
  filterMonitorings,
  FILTERS_DEFINITION,
  OsmMonitoringFiltersValues,
} from './OsmMonitoringList.filters';
import { MonitoringTableProvider, MonitoringTableActions } from '../common';

const INITIAL_FILTERS_STATE: OsmMonitoringFiltersValues = {
  status: [OsmMonitoringStatus.Current],
  date: undefined,
  type: [],
};

const INITIAL_TABLE_STATE: SuiTableState<OsmMonitoringDto> = {
  pageIndex: 0,
  pageSize: 10,
  sortBy: [{ id: 'monitoringDate', desc: true }],
};

export const OsmMonitoringList = () => {
  const examinerId = useExaminerId();
  const { permissions } = usePermissions();
  const [filtersState, setFiltersState] =
    useState<OsmMonitoringFiltersValues>(INITIAL_FILTERS_STATE);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const { data, isFetching } = useOsmMonitoringListQuery({ examinerId });
  const [updateFollowUp] = useUpdateOsmMonitoringFollowUpMutation();
  const [bulkDelete] = useBulkDeleteOsmMonitoringMutation();

  const filteredData = useMemo(() => filterMonitorings(data, filtersState), [data, filtersState]);

  const hasCurrentMonitorings = useMemo(
    () => filteredData.some((m) => m.status === OsmMonitoringStatus.Current),
    [filteredData]
  );

  const handleDelete = async (selectedIds: Set<number>) => {
    await bulkDelete({ ids: Array.from(selectedIds) }).unwrap();
    setSelectedIds(new Set());
  };

  const showAddButton = permissions.includes(Permission.UpdateMonitoringResults);
  const showDeleteButton = permissions.includes(Permission.DeleteAndRestoreMonitoringResults);

  return (
    <MonitoringTableProvider
      onUpdateFollowUp={updateFollowUp}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
    >
      <SegmentGroup>
        <FiltersBar
          value={filtersState}
          filters={FILTERS_DEFINITION}
          clearable={false}
          onFiltersChange={setFiltersState}
          attached
        />
        <MonitoringTableActions
          onDelete={handleDelete}
          archiveDisabled={!hasCurrentMonitorings}
          hasAddButton={showAddButton}
          hasDeleteButton={showDeleteButton}
        />
        <SuiTable
          disableFilters
          disableSortBy={false}
          data={filteredData}
          columns={columns}
          loading={isFetching}
          initialState={INITIAL_TABLE_STATE}
          tableProps={{
            selectable: true,
            sortable: true,
            'data-testid': 'monitoring-list-table',
          }}
          tableRowProps={(_, { row }) => ({
            negative: row.original.status === OsmMonitoringStatus.Deleted,
            warning: row.original.status === OsmMonitoringStatus.Archived,
            className: row.original.status === OsmMonitoringStatus.Deleted ? 'deleted-record' : '',
          })}
        />
      </SegmentGroup>
    </MonitoringTableProvider>
  );
};
